import React from "react";

import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { AutocompleteProps } from "@mui/material";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";

type PickedAutocompleteProps<T> = Pick<
  AutocompleteProps<T, true, false, false>,
  | "disabled"
  | "disableCloseOnSelect"
  | "getOptionDisabled"
  | "getOptionLabel"
  | "groupBy"
  | "isOptionEqualToValue"
  | "options"
  | "renderOption"
  | "renderTags"
>;

type AsyncAutocompleteProps<T> = Pick<
  AutocompleteProps<T, true, false, false>,
  "onClose" | "onOpen" | "open" | "loading"
>;

interface MultiAutocompleteInputProps<T> extends PickedAutocompleteProps<T> {
  value: T[];
  onChange: (value: T[]) => void;
  onBlur?: () => void;
  async?: AsyncAutocompleteProps<T>;
  placeholder?: string;
  "data-testid"?: string;
  error?: boolean;
  helperText?: string;
}

export function MultiAutocompleteInput<T>({
  options,
  value,
  "data-testid": dataTestId,
  disabled,
  onChange: handleChange,
  onBlur: handleBlur,
  placeholder,
  error,
  helperText,
  async,
  disableCloseOnSelect,
  getOptionDisabled,
  getOptionLabel,
  groupBy,
  isOptionEqualToValue,
  renderOption,
  renderTags,
}: MultiAutocompleteInputProps<T>) {
  return (
    <Autocomplete
      {...async}
      clearIcon={<CloseIcon sx={({ palette }) => ({ color: palette.grey[500] })} />}
      disabled={disabled}
      disableCloseOnSelect={disableCloseOnSelect}
      groupBy={groupBy}
      multiple
      onChange={(_event, newValue) => handleChange(newValue)}
      onBlur={handleBlur}
      getOptionDisabled={getOptionDisabled}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      options={options}
      popupIcon={<ExpandMoreIcon sx={({ palette }) => ({ color: palette.grey[500] })} />}
      renderInput={params => (
        <TextField
          {...params}
          InputProps={{
            ...params.InputProps,
            className: error ? "error" : undefined,
            endAdornment: (
              <React.Fragment>
                {async?.loading ? <CircularProgress color="inherit" size={20} sx={{ mr: 3 }} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
          data-testid={dataTestId}
          error={error}
          helperText={helperText}
          placeholder={value.length === 0 ? placeholder : ""}
        />
      )}
      size="small"
      renderOption={renderOption}
      renderTags={
        renderTags
          ? (value, getTagProps, ownerState) => {
              return (
                <div
                  style={{
                    minWidth: "100%",
                    height: "40px",
                    overflowY: "auto",
                  }}
                >
                  {renderTags(value, getTagProps, ownerState)}
                </div>
              );
            }
          : undefined
      }
      value={value}
    />
  );
}
